import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, EmptyState, Section, Kpi, Pill, Table, Th, Td, fmt } from '../components/ui-kit';

interface Lead { id: string; name: string; email: string; stage: string; revenue_eur?: number; }

export default function CRM() {
  const { getToken } = useXyqarAuth();
  const [items, setItems] = React.useState<Lead[] | null>(null);
  const [q, setQ] = React.useState('');
  React.useEffect(() => { (async () => { try { const t = await getToken().catch(() => ''); const j = await apiGet<{ leads: Lead[] }>('/api/v1/admin/crm/leads', t); setItems(j.leads ?? []); } catch { setItems([]); } })(); }, [getToken]);

  const stages = ['new', 'contacted', 'qualified', 'proposal', 'won', 'lost'];
  const filtered = (items ?? []).filter((lead) => !q || [lead.name, lead.email, lead.stage].some((value) => (value ?? '').toLowerCase().includes(q.toLowerCase())));
  const openStages = new Set(['new', 'contacted', 'qualified', 'proposal']);
  const openCount = filtered.filter((lead) => openStages.has((lead.stage ?? '').toLowerCase())).length;
  const wonCount = filtered.filter((lead) => (lead.stage ?? '').toLowerCase() === 'won').length;
  const revenue = filtered.reduce((sum, lead) => sum + (lead.revenue_eur ?? 0), 0);

  return (
    <div>
      <PageTitle kicker="Pipeline" title="CRM" />

      <Section title="At a glance" hint="Pipeline values reflect lead-side declared revenue only.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Visible leads" value={fmt(filtered.length)} hint={q ? 'filtered view' : 'full list'} />
          <Kpi accent="amber" label="Open pipeline" value={fmt(openCount)} />
          <Kpi accent="green" label="Won" value={fmt(wonCount)} />
          <Kpi label="Declared value" value={`${fmt(Math.round(revenue))} €`} />
        </div>
      </Section>

      <Section
        title="Stage board"
        right={(
          <input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Search name, email, stage…"
            className="rounded-md bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white placeholder:text-white/30 focus:border-white/30 focus:outline-none"
            aria-label="Search leads"
          />
        )}
      >
        {items === null && <Card>Loading…</Card>}
        {items && items.length === 0 && <EmptyState title="No leads yet" hint="Inbound leads created through /api/v1/crm/leads appear here." />}
        {items && items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {stages.map((stage) => {
              const inStage = filtered.filter((l) => l.stage === stage);
              return (
                <Card key={stage}>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">{stage}</p>
                    <Pill tone={stage === 'won' ? 'live' : stage === 'lost' ? 'alert' : 'warn'}>{inStage.length}</Pill>
                  </div>
                  <div className="flex flex-col gap-2 min-h-[7rem]">
                    {inStage.length === 0 && <p className="text-xs text-white/25">No lead in this stage.</p>}
                    {inStage.slice(0, 5).map((l) => (
                      <div key={l.id} className="text-xs border border-white/10 rounded p-2">
                        <p className="text-white truncate">{l.name}</p>
                        <p className="text-white/40 text-[10px] truncate">{l.email}</p>
                        {l.revenue_eur != null && <p className="text-green-400 mt-1">{l.revenue_eur.toLocaleString('fr-FR')} €</p>}
                      </div>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </Section>

      <Section title="Lead ledger" hint="Use this table for search, revenue sorting by eye, and quick deal inspection.">
        {items && items.length > 0 && (
          <Table>
            <thead>
              <tr><Th>Name</Th><Th>Email</Th><Th>Stage</Th><Th className="text-right">Revenue €</Th><Th>ID</Th></tr>
            </thead>
            <tbody>
              {filtered.map((lead) => (
                <tr key={lead.id} className="border-t border-white/5 hover:bg-white/5">
                  <Td><span className="text-white text-sm">{lead.name}</span></Td>
                  <Td><span className="text-white/70 text-xs font-mono">{lead.email}</span></Td>
                  <Td><Pill tone={lead.stage === 'won' ? 'live' : lead.stage === 'lost' ? 'alert' : 'warn'}>{lead.stage}</Pill></Td>
                  <Td className="text-right"><span className="text-white font-mono">{fmt(lead.revenue_eur ?? 0)}</span></Td>
                  <Td><span className="text-[11px] text-white/40 font-mono">{lead.id}</span></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>
    </div>
  );
}
